import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Video, Building2, Calendar, Loader2 } from "lucide-react";

const statusStyle = {
  confirmed: "bg-emerald-100 text-emerald-800",
  rescheduled: "bg-blue-100 text-blue-800",
  pending: "bg-amber-100 text-amber-800",
  completed: "bg-slate-200 text-slate-800",
  cancelled: "bg-red-100 text-red-700",
};

function isUpcoming(a) {
  const dt = new Date(`${a.date}T${a.start_time}:00`);
  return dt >= new Date() && ["confirmed", "rescheduled", "pending"].includes(a.status);
}

export default function PatientAppointments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("upcoming");
  const [cancelTarget, setCancelTarget] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [rescheduleSlots, setRescheduleSlots] = useState([]);
  const [pickedSlot, setPickedSlot] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/appointments"); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (tab === "upcoming") return items.filter(isUpcoming);
    if (tab === "completed") return items.filter((a) => a.status === "completed");
    if (tab === "cancelled") return items.filter((a) => a.status === "cancelled");
    return items;
  }, [items, tab]);

  const openReschedule = async (a) => {
    setRescheduleTarget(a);
    setPickedSlot(null);
    try {
      const { data } = await api.get(`/doctors/${a.doctor_id}/slots`);
      setRescheduleSlots(data.filter((s) => s.status === "available"));
    } catch { setRescheduleSlots([]); }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      await api.post(`/appointments/${cancelTarget.id}/cancel`);
      toast.success("Appointment cancelled");
      setCancelTarget(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doReschedule = async () => {
    if (!pickedSlot) return;
    setBusy(true);
    try {
      await api.post(`/appointments/${rescheduleTarget.id}/reschedule`, { slot_id: pickedSlot.id });
      toast.success("Rescheduled");
      setRescheduleTarget(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-slate-900">My appointments</h1>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-full bg-slate-100 p-1">
          <TabsTrigger value="upcoming" className="rounded-full" data-testid="tab-upcoming">Upcoming</TabsTrigger>
          <TabsTrigger value="completed" className="rounded-full" data-testid="tab-completed">Completed</TabsTrigger>
          <TabsTrigger value="cancelled" className="rounded-full" data-testid="tab-cancelled">Cancelled</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-6">
          {loading ? (
            <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : filtered.length === 0 ? (
            <div className="doccare-card p-10 text-center">
              <Calendar className="w-8 h-8 mx-auto text-slate-400" />
              <p className="mt-3 font-semibold text-slate-900">No {tab} appointments</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((a) => (
                <div key={a.id} className="doccare-card p-4" data-testid={`appt-${a.id}`}>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <img src={a.doctor.photo} alt="" className="w-14 h-14 rounded-xl object-cover" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-slate-900">{a.doctor.name}</p>
                          <p className="text-sm text-emerald-700">{a.doctor.specialty}</p>
                        </div>
                        <Badge className={`rounded-full ${statusStyle[a.status] || "bg-slate-100 text-slate-700"}`}>{a.status}</Badge>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {a.date} · {a.start_time}</span>
                        <span className="flex items-center gap-1">{a.consultation_type === "video" ? <><Video className="w-3 h-3" /> Video</> : <><Building2 className="w-3 h-3" /> In-person</>}</span>
                        <span className="font-mono text-[10px] bg-slate-100 rounded px-1.5 py-0.5">#{a.id.slice(0, 8)}</span>
                      </div>
                      {tab === "upcoming" && (
                        <div className="mt-4 flex gap-2">
                          <Button size="sm" variant="outline" className="rounded-full" onClick={() => openReschedule(a)} data-testid={`reschedule-${a.id}`}>Reschedule</Button>
                          <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50" onClick={() => setCancelTarget(a)} data-testid={`cancel-${a.id}`}>Cancel</Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Cancel dialog */}
      <Dialog open={!!cancelTarget} onOpenChange={() => setCancelTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel appointment?</DialogTitle>
            <DialogDescription>This will release the time slot back to availability.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)} className="rounded-xl">Keep</Button>
            <Button onClick={doCancel} disabled={busy} className="rounded-xl bg-red-600 hover:bg-red-700" data-testid="confirm-cancel">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cancel appointment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule dialog */}
      <Dialog open={!!rescheduleTarget} onOpenChange={() => setRescheduleTarget(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Reschedule appointment</DialogTitle>
            <DialogDescription>Pick an available time slot.</DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-y-auto grid grid-cols-3 gap-2">
            {rescheduleSlots.slice(0, 60).map((s) => (
              <button key={s.id} onClick={() => setPickedSlot(s)} className={`px-2 py-2 rounded-lg text-xs font-semibold border ${pickedSlot?.id === s.id ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-slate-200"}`} data-testid={`rslot-${s.id}`}>
                <div className="text-[10px] opacity-75">{s.date.slice(5)}</div>
                <div>{s.start_time}</div>
              </button>
            ))}
            {rescheduleSlots.length === 0 && <p className="col-span-3 text-sm text-slate-500 py-4 text-center">No available slots.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleTarget(null)} className="rounded-xl">Close</Button>
            <Button onClick={doReschedule} disabled={!pickedSlot || busy} className="rounded-xl" data-testid="confirm-reschedule">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reschedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
